// Client for the same-origin booking API (contract: prototypes/SITE.md).
export type Duration = 60 | 90 | 120;
export type Daypart = "morning" | "afternoon" | "evening";
export type Slot = {
  time: string;
  daypart: Daypart;
  courts: { id: number; free: boolean }[];
  courtsFree: number;
  price: number;
  peak: boolean;
};
export type Availability = { date: string; duration: Duration; slots: Slot[]; monthly?: { savePerHourUpTo: number; terms: string[] } };
export type Method = "card" | "gopay" | "va";
export type Booking = {
  id: string;
  ref: string;
  date: string;
  time: string;
  duration: Duration;
  court: number;
  name: string;
  phone: string;
  email?: string;
  players: string[];
  split: boolean;
  method: Method;
  total: number;
  share?: number;
  status: "confirmed" | "cancelled";
  createdAt: string;
};
export type BookingInput = {
  date: string;
  time: string;
  duration: Duration;
  court: number;
  name: string;
  phone: string;
  email?: string;
  players: string[];
  split: boolean;
  method: Method;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
  ) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: { accept: "application/json", ...(init.body ? { "content-type": "application/json" } : {}) },
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "network", "The connection dropped.");
  }
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* empty or non-JSON body */
  }
  if (!res.ok) {
    const err = body?.error ?? {};
    throw new ApiError(res.status, String(err.code ?? "http"), String(err.message ?? "Something went wrong."), err.fields);
  }
  return body as T;
}

export const api = {
  availability: (date: string, duration: Duration, signal?: AbortSignal) =>
    call<Availability>(`/api/availability?date=${encodeURIComponent(date)}&duration=${duration}`, { signal }),
  book: (input: BookingInput) =>
    call<{ booking: Booking; demo?: boolean }>("/api/bookings", { method: "POST", body: JSON.stringify(input) }),
  list: (phone: string) => call<{ bookings: Booking[] }>(`/api/bookings?phone=${encodeURIComponent(phone)}`),
  cancel: (id: string, phone: string) =>
    call<{ booking: Booking }>(`/api/bookings/${encodeURIComponent(id)}?phone=${encodeURIComponent(phone)}`, {
      method: "DELETE",
    }),
};
